import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY, estiloH3NY, estiloPNY } from "./nyHelpers";

// Template74: Día 2 Lower Manhattan 1 - Estatua, Ellis Island, Ferry. Contenido desde page.texts y page.images (servidor).
const Template74 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const introHtml = page?.texts?.[1]?.content;
	const estatuaTitle = page?.texts?.[2]?.content;
	const estatuaBodyHtml = page?.texts?.[4]?.content;
	const statenTitle = page?.texts?.[5]?.content;
	const statenBodyHtml = page?.texts?.[6]?.content;
	const batteryTitle = page?.texts?.[7]?.content;
	const batteryBodyHtml = page?.texts?.[8]?.content;
	const sabiasQueItems = parseSabiasQueItems(page?.texts?.[9]?.content);
	const comoLlegarLines = page?.texts?.[10]?.content;
	const comoLlegarTitle = page?.texts?.[11]?.content;
	const estatuaYellowBoxText = page?.texts?.[12]?.content;
	const estatuaIntroText = page?.texts?.[13]?.content;
	const statenSabiasQueRaw = page?.texts?.[14]?.content;
	const heroSubtitle = page?.texts?.[15]?.content;

	const statenSabiasQue = (() => {
		if (!statenSabiasQueRaw || typeof statenSabiasQueRaw !== "string")
			return null;
		const trim = statenSabiasQueRaw.trim();
		const match = trim.match(/^([^\s]+)\s+(.*)/s);
		return match ? { icono: match[1], texto: match[2].trim() } : null;
	})();

	const heroSrc = getImageSrc("lower-main");
	const estatuaSrc = getImageSrc("estatua");
	const statenSrc = getImageSrc("staten");
	const heroStickerSrc = getImageSrc("lower-manhattan-sticker");
	// Sticker de la Estatua: usar misma imagen que la foto (estatua.png) para evitar mostrar otra por id
	const estatuaStickerSrc =
		getImageSrc("estatua-sticker") || getImageSrc("estatua");
	const logoSrc = getCanvasImageUrl("logo");

	const comoLlegarItems = comoLlegarLines
		? String(comoLlegarLines)
				.split(/\n/)
				.map((s) => s.trim())
				.filter(Boolean)
		: [];

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "0px !important",
				},
				"& .margin": {
					marginBottom: "8px !important",
				},
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1053,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			{heroSrc && (
				<div data-block-id="image-0" style={{ position: "relative" }}>
					<img
						src={heroSrc}
						alt="Lower Manhattan"
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
					{heroSubtitle && (
						<h1
							style={{
								fontFamily: '"Poppins", sans-serif',
								fontSize: 32,
								textAlign: "center",
								position: "absolute",
								bottom: "0",
								margin: 10,
								left: 20,
								color: "#fff",
								textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
							}}
						>
							{heroSubtitle}
						</h1>
					)}
					{heroStickerSrc && (
						<img
							src={heroStickerSrc}
							alt="Lower Manhattan"
							style={{ width: 90, position: "absolute", bottom: 50, left: 25 }}
						/>
					)}
				</div>
			)}

			{introHtml && (
				<div data-block-id="text-1" style={{ padding: "10px 12px", position: "relative" }}>
					<div
						style={{
							fontFamily: '"Poppins", sans-serif',
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
						dangerouslySetInnerHTML={{ __html: introHtml }}
					/>
				</div>
			)}

			{(estatuaTitle || estatuaBodyHtml || estatuaSrc || estatuaIntroText) && (
				<div
					data-block-id="text-2"
					style={{
						padding: "0 12px",
						display: "flex",
						flexDirection: "column",
						gap: 12,
						marginTop: 8,
					}}
				>
					<div style={{ display: "flex", gap: 16, position: "relative" }}>
						<div
							style={{ display: "flex", flexDirection: "column", width: "40%" }}
						>
							{estatuaSrc && (
								<img
									src={estatuaSrc}
									alt="Libertad"
									style={{
										width: "100%",
										height: 100,
										borderRadius: 12,
										objectFit: "cover",
									}}
								/>
							)}
							{estatuaYellowBoxText && (
								<div
									style={{
										position: "absolute",
										bottom: isSmallScreen ? 80 : 0,
										left: isSmallScreen ? 8 : 60,
										backgroundColor: "#fff8db",
										border: "2px solid #f5c400",
										borderRadius: "16px 16px 16px 0px",
										padding: "10px 12px",
										fontSize: 11,
										fontFamily: '"Poppins", sans-serif',
										color: "#444",
										boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
										maxWidth: 120,
										zIndex: 6,
									}}
								>
									{estatuaYellowBoxText}
								</div>
							)}
							{estatuaStickerSrc && (
								<img
									src={estatuaStickerSrc}
									alt="Estatua"
									style={{
										width: 100,
										position: "absolute",
										left: -30,
										bottom: isSmallScreen ? 0 : -30,
										zIndex: 5,
									}}
								/>
							)}
						</div>
						{sabiasQueItems[0] && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? 52 : 40,
									left: isSmallScreen ? "32%" : "34%",
									zIndex: 30,
								}}
							>
								<SabiasQueNY
									icono={sabiasQueItems[0].icono}
									texto={sabiasQueItems[0].texto}
								/>
							</div>
						)}
						{sabiasQueItems[1] && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? 12 : 0,
									left: isSmallScreen ? "32%" : "34%",
									zIndex: 20,
								}}
							>
								<SabiasQueNY
									icono={sabiasQueItems[1].icono}
									texto={sabiasQueItems[1].texto}
								/>
							</div>
						)}
						{sabiasQueItems[2] && (
							<div
								style={{
									position: "absolute",
									top: 0,
									left: isSmallScreen ? "32%" : "34%",
									zIndex: 20,
								}}
							>
								<SabiasQueNY
									icono={sabiasQueItems[2].icono}
									texto={sabiasQueItems[2].texto}
								/>
							</div>
						)}
						<div
							style={{ width: "60%", display: "flex", flexDirection: "column" }}
						>
							{estatuaTitle && <h3 style={estiloH3NY}>{estatuaTitle}</h3>}
							{estatuaIntroText && <p style={estiloPNY}>{estatuaIntroText}</p>}
							{estatuaBodyHtml && (
								<div
									style={{
										backgroundColor: "#e0f7fa",
										border: "3px solid #59c3d8",
										borderRadius: 12,
										fontFamily: '"Poppins", sans-serif',
										width: "100%",
										padding: "4px 10px",
										marginTop: 8,
									}}
								>
									<div
										style={{ ...estiloPNY, margin: 0, fontSize: 11 }}
										dangerouslySetInnerHTML={{ __html: estatuaBodyHtml }}
									/>
								</div>
							)}
						</div>
					</div>

					{(statenTitle || statenBodyHtml || statenSrc || statenSabiasQue) && (
						<div
							data-block-id="text-3"
							style={{
								backgroundColor: "#e0f7fa",
								padding: 0,
								borderRadius: 12,
								display: "flex",
								gap: 16,
								alignItems: "center",
								position: "relative",
							}}
						>
							<div style={{ paddingLeft: 8 }}>
								{statenTitle && <h3 style={estiloH3NY}>{statenTitle}</h3>}
								{statenBodyHtml && (
									<div
										style={estiloPNY}
										dangerouslySetInnerHTML={{ __html: statenBodyHtml }}
									/>
								)}
							</div>
							{statenSrc && (
								<img
									src={statenSrc}
									alt="Staten Island"
									style={{ width: 100 }}
								/>
							)}
							{statenSabiasQue && (
								<div
									style={{
										position: "absolute",
										top: isSmallScreen ? -20 : -12,
										right: isSmallScreen ? "50%" : "50%",
										zIndex: 10,
									}}
								>
									<SabiasQueNY
										icono={statenSabiasQue.icono}
										texto={statenSabiasQue.texto}
									/>
								</div>
							)}
						</div>
					)}

					{(batteryTitle ||
						batteryBodyHtml ||
						comoLlegarTitle ||
						comoLlegarItems.length > 0) && (
						<div
							data-block-id="text-4"
							style={{
								display: "flex",
								gap: isSmallScreen ? 8 : 20,
								flexDirection: isSmallScreen ? "column" : "row",
							}}
						>
							<div
								style={{
									display: "flex",
									width: isSmallScreen ? "100%" : "40%",
									gap: 16,
								}}
							>
								<div style={{ position: "relative" }}>
									{batteryTitle && <h3 style={estiloH3NY}>{batteryTitle}</h3>}
									{batteryBodyHtml && (
										<div
											style={estiloPNY}
											dangerouslySetInnerHTML={{ __html: batteryBodyHtml }}
										/>
									)}
								</div>
							</div>
							{(comoLlegarTitle || comoLlegarItems.length > 0) && (
								<div
									style={{
										backgroundColor: "#e0f7fa",
										border: "3px solid #59c3d8",
										borderRadius: 12,
										padding: "8px 20px",
										fontSize: 12,
										fontFamily: '"Poppins", sans-serif',
										color: "#333",
										marginBottom: isSmallScreen ? 12 : 0,
										width: isSmallScreen ? "100%" : "60%",
									}}
								>
									{comoLlegarTitle && (
										<h4
											style={{
												margin: 0,
												marginBottom: 8,
												fontSize: 13,
												fontWeight: 700,
												display: "flex",
												alignItems: "center",
												gap: 6,
												color: "#007a8d",
												textTransform: "uppercase",
											}}
										>
											{comoLlegarTitle}
										</h4>
									)}
									{comoLlegarItems.length > 0 ? (
										<ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.5 }}>
											{comoLlegarItems.map((line, i) => (
												<li key={i} style={{ marginBottom: 4 }}>
													{line.includes("–") ? (
														<>
															<strong>{line.split("–")[0].trim()}</strong>
															{" – "}
															<em>
																{line.split("–").slice(1).join("–").trim()}
															</em>
														</>
													) : (
														line
													)}
												</li>
											))}
										</ul>
									) : null}
								</div>
							)}
						</div>
					)}
				</div>
			)}
		</Box>
	);
};

export default Template74;
